import { TCommonData, TWidget, TViewCommonOption } from '@libs/types';
import clsx from 'clsx';
import React, { Activity, CSSProperties, ElementType, MouseEventHandler, Ref, TransitionEventHandler, UIEventHandler } from 'react';
import { useThemeHook } from '../hooks/themeHook';
import { ORIENTATION_ENUM, TCoreLayoutOptions } from '../types/options';
import { buildLayoutCssVars } from './buildLayoutCssVars';
import { mergeWithTheme } from '../utils/styles';
import styles from './layout.module.css';

export type TCoreLayoutStyleProps = Pick<
    TCoreLayoutOptions,
    | 'disabled'
    | 'hovered'
    | 'error'
    | 'pressed'
    | 'orientation'
    | 'lineX'
    | 'lineY'
    | 'backgroundColor'
    | 'borderColor'
    | 'width'
    | 'minWidth'
    | 'height'
    | 'minHeight'
    | 'borderRadius'
    | 'borderStroke'
    | 'boxShadow'
    | 'overflow'
    | 'padding'
    | 'margin'
    | 'gap'
    | 'flexShrink'
    | 'opacity'
>;

export type TCoreLayoutViewProps = {
    /**
     * Used solely for theme resolution via useThemeHook. No behavior or interaction logic is derived from it here.
     * Optional: when omitted, a minimal widget is synthesized internally. Provide it only when the widget
     * originates server-side (so theme styles via `widget.options.style` are honored).
     */
    widget?: TWidget<TCommonData, TCoreLayoutOptions>;
    uid?: string;
    children?: React.ReactNode;
    /** Style overrides merged on top of the resolved theme palette via mergeWithTheme. */
    styleProps?: TCoreLayoutStyleProps;
    /** When true, width uses `--cw-layout-width` as-is instead of subtracting horizontal margins. */
    fullWidth?: boolean;
    onClick?: MouseEventHandler<HTMLDivElement>;
    onScroll?: UIEventHandler<HTMLDivElement>;
    onTransitionEnd?: TransitionEventHandler<HTMLDivElement>;
    role?: React.AriaRole;
    as?: ElementType;
    className?: string;
    /** DOM id applied to the root element. */
    id?: string;
    /** Ref to the root element. */
    ref?: Ref<HTMLDivElement>;
} & Pick<TViewCommonOption, 'isHide' | 'isDisabled' | 'isError' | 'styleName' | 'style' | 'parentStyleNames'>;

/**
 * Composable building block for use inside complex widgets.
 * Owns only the theme hook (useThemeHook) to keep the hook count low
 * when embedded inside larger widget trees.
 *
 * Accepts pre-resolved behavior props (isDisabled, isHide, onClick) from
 * the parent, making it suitable for scenarios where behavior state is
 * already managed one level up.
 *
 * `widget` is optional — when omitted, a minimal widget is synthesized from `uid`,
 * which is sufficient for standalone layout usages that don't need server-driven theme styles.
 *
 * @param className - A CSS class name to be applied to the root element. We should use this for passing animations.
 */
const CoreLayoutView = ({
    widget,
    uid,
    isHide,
    isDisabled,
    isError,
    onClick,
    onScroll,
    onTransitionEnd,
    children,
    styleProps,
    fullWidth,
    className,
    style,
    styleName,
    parentStyleNames,
    role,
    as: Tag = 'div',
    id,
    ref,
}: TCoreLayoutViewProps) => {
    const resolvedWidget = widget ?? ({ uid: uid ?? '', data: {}, options: {} } as TWidget<TCommonData, TCoreLayoutOptions>);
    // `style` and `parentStyleNames` come from props only — the View never reads them off `widget.options`.
    // The Widget container (CoreLayoutWidget) is responsible for extracting `widget.options?.style`
    // and passing it as an explicit `style` prop.
    const enrichedWidget = { ...resolvedWidget, options: { ...resolvedWidget.options, style: styleName, parentStyleNames } };
    const { palette: themePalette } = useThemeHook(enrichedWidget);
    const palette: TCoreLayoutOptions = mergeWithTheme(themePalette, styleProps);

    const isBox = (palette.orientation ?? ORIENTATION_ENUM.VERTICAL) === ORIENTATION_ENUM.BOX;
    const isFullWidth = !!fullWidth;
    const cssVars = buildLayoutCssVars(palette, { isError, isDisabled });

    const isClickable = !!onClick && !isDisabled;
    const isNativeButton = Tag === 'button';
    const resolvedRole = isNativeButton ? undefined : (role ?? (isClickable ? 'button' : undefined));
    const finalUid = uid ?? widget?.uid;
    const BoxInner = (isNativeButton ? 'span' : 'div') as ElementType;
    const buttonProps = isNativeButton ? { type: 'button' as const, disabled: isDisabled } : undefined;

    if (isBox) {
        return (
            <Activity mode={isHide ? 'hidden' : 'visible'}>
                <Tag
                    ref={ref}
                    id={id}
                    data-test={finalUid}
                    className={clsx(styles.layout, styles.layoutBox, isClickable && styles.clickable, isDisabled && styles.disabled, className)}
                    style={{ ...(cssVars as CSSProperties), ...style }}
                    onClick={isClickable ? onClick : undefined}
                    onScroll={onScroll}
                    onTransitionEnd={onTransitionEnd}
                    role={resolvedRole}
                    {...buttonProps}
                >
                    <BoxInner className={styles.layoutBoxInner}>{children}</BoxInner>
                </Tag>
            </Activity>
        );
    }

    return (
        <Activity mode={isHide ? 'hidden' : 'visible'}>
            <Tag
                ref={ref}
                id={id}
                data-test={finalUid}
                className={clsx(styles.layout, isFullWidth && styles.fullWidth, isClickable && styles.clickable, isDisabled && styles.disabled, className)}
                style={{ ...(cssVars as CSSProperties), ...style }}
                onClick={isClickable ? onClick : undefined}
                onScroll={onScroll}
                onTransitionEnd={onTransitionEnd}
                role={resolvedRole}
                {...buttonProps}
            >
                {children}
            </Tag>
        </Activity>
    );
};

export { CoreLayoutView };
